import { useMutation } from "@tanstack/react-query";

import type { SellerMessageDTO, SellerPreferencesUpdate } from "@recruiter/shared";

import { log } from "../log";
import { queryClient } from "../query-client";
import { sellerKeys, unwrap, updateThreadDetailCache } from "../queries/seller";
import { postSellerMessage, saveSellerPreferences } from "../seller-client";

type SendVars = {
  threadId: string;
  body: string;
  sellerId: string;
  attachmentFileIds?: string[];
};
type SendContext = { optimisticId: string; sellerId: string; threadId: string };

// Append the talent's own just-sent message to the open thread's cached detail so
// it shows the instant they hit Send. No-ops when the detail isn't cached
// (updateThreadDetailCache never fabricates a partial), and idempotent by id —
// mirrors the dispatcher's append for a pushed `seller.message`.
export function appendOptimisticSellerMessage(
  sellerId: string,
  threadId: string,
  message: SellerMessageDTO,
): void {
  updateThreadDetailCache(sellerId, threadId, (current) =>
    current.messages.some((m) => m.id === message.id)
      ? current
      : { ...current, messages: [...current.messages, message] },
  );
}

// Roll the optimistic bubble back out of the cached detail when the POST fails.
export function removeOptimisticSellerMessage(
  sellerId: string,
  threadId: string,
  id: string,
): void {
  updateThreadDetailCache(sellerId, threadId, (current) => ({
    ...current,
    messages: current.messages.filter((m) => m.id !== id),
  }));
}

// The talent's reply (mirrors web's mutation hooks AND its optimistic outbound
// bubble). POSTs the message (202 → enqueues STERLING's turn) and surfaces
// failures as SellerQueryError (doctrine 1.5).
//
// Optimistic write: the worker pushes back only STERLING's reply — it does NOT
// echo the talent's own message — so without an optimistic insert the talent's
// message wouldn't appear until the next authoritative GET (i.e. a refresh). We
// append it locally in `onMutate`; the backend persists it synchronously in the
// POST, so any later detail re-GET (a `seller.thread.update` invalidation)
// full-replaces the messages array and the `local-…` bubble resolves to the real
// server row with no double bubble. STERLING's reply then arrives as its own
// `seller.message` event and simply appends after it. A failed POST rolls the
// bubble back (the composer keeps the draft so the talent can retry).
export function useSendSellerMessage() {
  return useMutation({
    mutationFn: async (vars: SendVars) =>
      unwrap(
        await postSellerMessage(
          vars.threadId,
          vars.body,
          vars.sellerId,
          vars.attachmentFileIds ?? [],
        ),
      ),
    onMutate: (vars: SendVars): SendContext => {
      const optimisticId = `local-${Date.now()}`;
      appendOptimisticSellerMessage(vars.sellerId, vars.threadId, {
        id: optimisticId,
        sender: "seller",
        author: "talent",
        body: vars.body,
        sent_at: new Date().toISOString(),
      });
      return { optimisticId, sellerId: vars.sellerId, threadId: vars.threadId };
    },
    onSuccess: (_data, vars) => {
      // The message + its linked attachments were persisted synchronously by the
      // 202 POST. When the talent attached files, re-GET the authoritative detail
      // so their own bubble shows its attachment chips (the optimistic bubble
      // carries none). Text-only replies keep the optimistic-only path (no extra
      // fetch); STERLING's reply always arrives separately over Pusher.
      if (vars.attachmentFileIds && vars.attachmentFileIds.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: sellerKeys.thread(vars.sellerId, vars.threadId),
        });
      }
      // The same POST stamped their first reply, which moves a "New request" to a
      // conversation and adds them to the project's "Replied" count. Both are derived
      // server-side, so re-read the rail and this project's overview.
      void queryClient.invalidateQueries({ queryKey: sellerKeys.threads(vars.sellerId) });
      void queryClient.invalidateQueries({
        queryKey: sellerKeys.overview(vars.sellerId, vars.threadId),
      });
    },
    onError: (error, _vars, context) => {
      if (context) {
        removeOptimisticSellerMessage(context.sellerId, context.threadId, context.optimisticId);
      }
      log.warn("seller-mutation", "reply failed", {
        message: error instanceof Error ? error.message : String(error),
      });
    },
  });
}

type SavePreferencesVars = { sellerId: string; body: SellerPreferencesUpdate };

// Save the talent's whole preference set from the picker. It applies at once, so the
// response IS the new live set: written straight into the cache (no extra GET). Every
// open project overview carries a "fits your rates" line computed from these, and the Pro
// insights count the projects under them, so those re-read. Failures surface as
// SellerQueryError in the drawer (doctrine 1.5).
export function useSaveSellerPreferences() {
  return useMutation({
    mutationFn: async (vars: SavePreferencesVars) =>
      unwrap(await saveSellerPreferences(vars.body, vars.sellerId)),
    onSuccess: (view, vars) => {
      queryClient.setQueryData(sellerKeys.preferences(vars.sellerId), view);
      void queryClient.invalidateQueries({ queryKey: sellerKeys.overviews(vars.sellerId) });
      void queryClient.invalidateQueries({ queryKey: sellerKeys.insights(vars.sellerId) });
    },
    onError: (error) => {
      log.warn("seller-mutation", "preferences save failed", {
        message: error instanceof Error ? error.message : String(error),
      });
    },
  });
}
